import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ItemLocation } from '../types/item-location.types';
import { itemLocationApi } from '../api/itemLocationApi';

interface ItemLocationsSectionProps {
  itemId: string;
  itemName: string;
}

const ItemLocationsSection: React.FC<ItemLocationsSectionProps> = ({ itemId }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [itemLocations, setItemLocations] = useState<ItemLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Format date to a readable format
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Check if batch is expiring soon (within 30 days)
  const isExpiringSoon = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    return expiry <= thirtyDaysFromNow && expiry >= now;
  };

  // Check if batch is expired
  const isExpired = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    return expiry < now;
  };

  // Get balance color based on stock level
  const getBalanceColor = (balance: number) => {
    if (balance <= 0) return 'error';
    if (balance <= 10) return 'warning';
    return 'success';
  };

  // Get balance label
  const getBalanceLabel = (balance: number) => {
    if (balance <= 0) return 'Out of Stock';
    if (balance <= 10) return 'Low Stock';
    return 'In Stock';
  };

  // Load item locations
  const loadItemLocations = async () => {
    try {
      setLoading(true);
      const data = await itemLocationApi.getItemLocationsByItemId(itemId, true);
      setItemLocations(data);
    } catch (error) {
      console.error('Error loading item locations:', error);
      enqueueSnackbar('Failed to load item locations', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Calculate total balance
  const totalBalance = itemLocations.reduce((sum, location) => sum + location.balance, 0);

  // Load item locations on component mount
  useEffect(() => {
    loadItemLocations();
  }, [itemId]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Stock by Locations</Typography>
          <Typography variant="body2" color="textSecondary">
            Total Stock: <strong>{totalBalance.toLocaleString()}</strong> units
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadItemLocations} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : itemLocations.length === 0 ? (
        <Box textAlign="center" p={3}>
          <Typography variant="body2" color="textSecondary">
            No stock found at any location
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell>Batch Number</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemLocations.map((itemLocation) => (
                <TableRow key={itemLocation.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {itemLocation.location?.name || 'Unknown Location'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {itemLocation.location?.code || itemLocation.locationId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {itemLocation.batchNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {isExpired(itemLocation.expiryDate) && (
                        <WarningIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                      )}
                      {isExpiringSoon(itemLocation.expiryDate) && !isExpired(itemLocation.expiryDate) && (
                        <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
                      )}
                      <Typography variant="body2">
                        {formatDate(itemLocation.expiryDate)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={itemLocation.balance <= 0 ? 'error.main' : itemLocation.balance <= 10 ? 'warning.main' : 'text.primary'}
                    >
                      {itemLocation.balance.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {/* Stock Status */}
                      <Chip
                        label={getBalanceLabel(itemLocation.balance)}
                        color={getBalanceColor(itemLocation.balance)}
                        size="small"
                      />
                      
                      {/* Expiry Status */}
                      {isExpired(itemLocation.expiryDate) && (
                        <Chip
                          label="Expired"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {isExpiringSoon(itemLocation.expiryDate) && !isExpired(itemLocation.expiryDate) && (
                        <Chip
                          label="Expiring Soon"
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      
                      {/* Active Status */}
                      {!itemLocation.isActive && (
                        <Chip
                          label="Inactive"
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              
              {/* Summary Row */}
              {itemLocations.length > 1 && (
                <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                  <TableCell>{itemLocations.length} Batches</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {totalBalance.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getBalanceLabel(totalBalance)}
                      color={getBalanceColor(totalBalance)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ItemLocationsSection;
